import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../api/errors";
import {
  inventoryApi,
  type AdjustBody,
  type InventoryListQuery,
} from "../api/inventory";
import type {
  AdjustResult,
  InventoryListPage,
  MovementHistory,
  ThresholdResult,
} from "../api/types";

/**
 * Reading stock, and moving it.
 *
 * Every query key starts with "inventory", which is what makes the
 * invalidation after an adjustment a single statement rather than a list of
 * keys somebody has to keep in step with the screens. The failure mode this
 * guards against is the list still showing 12 for an item whose history panel,
 * one row below, has just recorded it going to 17.
 */
export const inventoryKeys = {
  all: ["inventory"] as const,
  list: (query: InventoryListQuery) => ["inventory", "list", query] as const,
  history: (itemId: string) => ["inventory", "history", itemId] as const,
};

/** How many stock rows one page of the table shows. */
export const INVENTORY_PAGE_SIZE = 25;

export function useInventoryList(query: InventoryListQuery) {
  return useQuery<InventoryListPage>({
    queryKey: inventoryKeys.list(query),
    queryFn: ({ signal }) => inventoryApi.list(query, signal),
    // The previous page stays on screen while the next one loads, so paging
    // and typing do not blank the table between answers.
    placeholderData: (previous) => previous,
  });
}

/** One item's movements, fetched only when its drawer is actually open.
 *
 * `enabled` is the point: a history request per row would be a query per line
 * of the table for a panel nobody has opened. */
export function useMovementHistory(itemId: string | null) {
  return useQuery<MovementHistory>({
    queryKey: inventoryKeys.history(itemId ?? "none"),
    queryFn: ({ signal }) => inventoryApi.history(itemId as string, signal),
    enabled: Boolean(itemId),
  });
}

/**
 * Move one item's stock, then make every view of it agree again.
 *
 * NOTHING IS ASSUMED BEFORE THE SERVER ANSWERS. There is no optimistic update:
 * the new quantity is not written into the cache when the button is pressed,
 * only when the backend has said what the item now holds. Stock is money, and
 * a screen showing 17 for the moment before a refusal arrives is a screen an
 * operator can act on twice.
 */
export function useAdjustStock(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation<AdjustResult, unknown, AdjustBody>({
    mutationFn: (body) => inventoryApi.adjust(itemId, body),

    onSuccess: async () => {
      // Everything: the list, its totals, and this item's history — an
      // adjustment moves the row between availability filters and adds an
      // event the drawer beside it should already be showing.
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },

    onError: async (error) => {
      // A conflict means the item moved while the dialog was open, so what is
      // on screen is stale. Reload and stop — the request is NEVER resent
      // automatically. Deciding what to do from the true quantity is the
      // operator's, and a silent retry would be this client deciding for them.
      if (error instanceof ApiError && error.kind === "conflict") {
        await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      }
    },
  });
}

/** Configure — or clear — when one item counts as running low.
 *
 * Deliberately a separate mutation from the adjustment above, because it is a
 * separate kind of thing: no unit moves, so no movement is written and the
 * history is untouched. Only the list is refreshed. */
export function useSetThreshold(itemId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    ThresholdResult,
    unknown,
    { expectedVersion: number; threshold: number | null }
  >({
    mutationFn: ({ expectedVersion, threshold }) =>
      inventoryApi.setThreshold(itemId, expectedVersion, threshold),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },

    onError: async (error) => {
      if (error instanceof ApiError && error.kind === "conflict") {
        await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      }
    },
  });
}
